/**
 * 滋賀県の背景ツアー「滋賀めぐり」コンテンツ。
 *
 * 軸は季節。春の八幡堀から、冬の延暦寺へ。
 *
 * 白鬚神社の湖中鳥居について。
 *  佐賀（大魚神社の海中鳥居）で水中の鳥居をすでに使っている。
 *  佐賀は複数基が沖へ連なり、干潮で干潟が現れる海の絵。
 *  白鬚は1基だけ、足元が水に没していて、波のない鏡のような湖面に対岸の山並みが見える。
 *  この4点を英語記述に明示して分けている。鳥居が主題なので否定文は入れていない。
 *
 * 比叡山延暦寺について。
 *  根本中堂の所在は滋賀県大津市坂本本町。京都府と取り違えない。
 *  世界遺産の登録名「古都京都の文化財（京都市、宇治市、大津市）」にも大津市が入っている。
 *
 * 琵琶湖について。
 *  県公式の文言は「日本最大の淡水湖」。この形のまま使う。「日本最大の湖」と短縮しない。
 *  「日本最古の湖」は県公式の断定形なのでそのまま使える。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 彦根城を「現存12天守のひとつ」と書かない。施設公式・市公式で確認できなかった。
 *    書けるのは「現存する三層複合式の天守」「昭和27年3月29日に国宝指定」まで。
 *  - 白鬚神社を厳島神社と比べる書き方をしない（厳島神社は広島で使用済み）。
 *  - メタセコイア並木の絵に停まった車を描かない。高島市が路上駐停車を禁じている。
 *  - 石山寺の多宝塔を「五重塔」と書かない。
 *  - 伊吹山の標高は米原市の公式で確認できなかった。数字を書かない。
 *  - 彦根市の公式キャラクター、ドラマや映画の作品名は出さない。
 */

import type { StageTour } from '../types';

export const shigaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'hachiman-bori',
      name: '八幡堀',
      area: '近江八幡市・春の朝',
      image: '/assets/scenery/shiga/scenes/01_hachiman_bori.webp',
      hotspots: [
        {
          id: 'hachiman-hidetsugu',
          x: 0.46,
          y: 0.44,
          title: '城のふもとに掘られた堀',
          body: '豊臣秀次の八幡山城のふもとにあり、城の防備と、琵琶湖を往来する船をすべて八幡に寄らせるために作られた堀です。一度は埋め立ての話が出るほど荒れ果てましたが、地元の人たちが10年もの年月をかけてきれいにしました。',
          source: 'https://www.omi8.com/stories/detail_68.html',
          mapQuery: '八幡堀 近江八幡市',
        },
        {
          id: 'hachiman-judenken',
          x: 0.74,
          y: 0.34,
          title: '国が選んだ商家町の保存地区',
          body: '文化庁の重要伝統的建造物群保存地区一覧に「近江八幡市八幡」として掲載されています。種別は商家町、選定は平成3年4月30日、面積は13.1ヘクタールです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '近江八幡市八幡 重要伝統的建造物群保存地区',
        },
        {
          id: 'hachiman-wasen',
          x: 0.22,
          y: 0.5,
          title: '三十分の和舟めぐり',
          body: '和舟に乗って、およそ三十分で堀をめぐります。エンジン船と手漕ぎ船があり、揺れる柳と白壁の土蔵が立ち並ぶ堀を舟の上から眺められます。春は桜につづいて菖蒲、夏は柳、秋は紅葉、冬は雪景色が堀沿いを彩ります。',
          source: 'https://www.omi8.com/spot/detail_1030.html',
          mapQuery: '八幡堀めぐり 和舟',
        },
      ],
    },
    {
      id: 'hikone-jo',
      name: '彦根城と玄宮園',
      area: '彦根市・春の昼',
      image: '/assets/scenery/shiga/scenes/02_hikone_jo.webp',
      hotspots: [
        {
          id: 'hikone-kokuho',
          x: 0.46,
          y: 0.36,
          title: '三層の天守は国宝',
          body: '彦根城の天守は現存する三層複合式で、高さは石垣五メートルを含めて20.5メートル。昭和27年3月29日に国宝に指定されました。江戸時代の政治のしくみを伝える代表例とされています。',
          source: 'https://hikonecastle.com/index.html',
          mapQuery: '彦根城 天守',
        },
        {
          id: 'hikone-sanju',
          x: 0.74,
          y: 0.52,
          title: '三重の堀と人工の川',
          body: '慶長九年に工事が始まり、完成までおよそ二十年。城内はおよそ五十万平方メートルの広さで、かつては三重の堀と人工河川を備えていました。昭和26年に史跡、31年に特別史跡に指定されています。',
          source: 'https://hikonecastle.com/index.html',
          mapQuery: '彦根城 内堀 中堀',
        },
        {
          id: 'hikone-62do',
          x: 0.22,
          y: 0.48,
          title: '62度の不規則な階段',
          body: '本丸まではおよそ140段の石段があり、高低差はおよそ50メートル。天守内部の階段は一層から二層が62度、二層から三層が60度という急勾配です。敵兵が一気に駆け上がれないよう、微妙に不規則な階段になっています。',
          source: 'https://hikonecastle.com/index.html',
          mapQuery: '玄宮園 彦根市',
        },
      ],
    },
    {
      id: 'shirahige',
      name: '白鬚神社の湖中鳥居',
      area: '高島市・初夏の夜明け',
      image: '/assets/scenery/shiga/scenes/03_shirahige.webp',
      hotspots: [
        {
          id: 'shirahige-kochu',
          x: 0.46,
          y: 0.44,
          title: '湖の中に立つ朱の鳥居',
          body: '朱塗りの鳥居が湖中に建っています。社はJR近江高島駅から東南へおよそ2キロメートル、琵琶湖の西岸に面して鎮座しています。',
          source: 'https://www.city.takashima.lg.jp/soshiki/bunkasports/bunkazaika/1/1/666.html',
          mapQuery: '白鬚神社 高島市',
        },
        {
          id: 'shirahige-saiko',
          x: 0.74,
          y: 0.34,
          title: '近江でいちばん古い社',
          body: '近江最古の社とされ、白鬚さん、明神さんと親しまれてきました。延命長寿の神様として知られています。境内には与謝野鉄幹・晶子の歌碑や、松尾芭蕉の句碑が立っています。',
          source: 'https://www.city.takashima.lg.jp/soshiki/bunkasports/bunkazaika/1/1/666.html',
          mapQuery: '白鬚神社 境内 歌碑',
        },
        {
          id: 'shirahige-tenbodai',
          x: 0.22,
          y: 0.5,
          title: '鳥居を撮るための展望台',
          body: '湖中の鳥居を撮影するために、社務所前に展望台「藍湖白鬚台」が設けられています。毎年九月五日・六日の秋大祭は白鬚まつりと呼ばれ、多くの参拝者が訪れます。',
          source: 'https://www.city.takashima.lg.jp/soshiki/bunkasports/bunkazaika/1/1/666.html',
          mapQuery: '藍湖白鬚台 白鬚神社',
        },
      ],
    },
    {
      id: 'chikubushima',
      name: '竹生島',
      area: '長浜市・初夏の昼',
      image: '/assets/scenery/shiga/scenes/04_chikubushima.webp',
      hotspots: [
        {
          id: 'chikubu-fune',
          x: 0.46,
          y: 0.44,
          title: '汽船で25分から40分',
          body: '琵琶湖に浮かぶ島で、長浜・彦根・今津の各港から汽船でおよそ25分から40分かけて渡ります。長浜港・今津港からは琵琶湖汽船、彦根港からはオーミマリンの船が出ています。',
          source: 'https://www.chikubushima.jp/',
          mapQuery: '竹生島 長浜市',
        },
        {
          id: 'chikubu-hogonji',
          x: 0.74,
          y: 0.34,
          title: '聖武天皇の夢から始まった寺',
          body: '宝厳寺は神亀元年、聖武天皇が天照皇大神より島に寺を建てよとのお告げを受け、行基によって開かれたと伝わります。本尊は弁才天で、国宝の唐門をはじめ多くの文化財が残ります。',
          source: 'https://www.chikubushima.jp/',
          mapQuery: '宝厳寺 竹生島 唐門',
        },
        {
          id: 'chikubu-saigoku',
          x: 0.22,
          y: 0.5,
          title: '西国三十三所の第三十番',
          body: '宝厳寺は西国三十三所観音霊場の第三十番札所です。「1300年つづく日本の終活の旅～西国三十三所観音巡礼～」は日本遺産に認定されています。境内の観音堂と舟廊下は重要文化財です。',
          source: 'https://www.chikubushima.jp/',
          mapQuery: '竹生島 西国三十三所',
        },
      ],
    },
    {
      id: 'samegai',
      name: '醒井の梅花藻',
      area: '米原市・盛夏の午前',
      image: '/assets/scenery/shiga/scenes/05_samegai.webp',
      hotspots: [
        {
          id: 'samegai-14do',
          x: 0.46,
          y: 0.46,
          title: '年じゅう14度の川',
          body: '梅花藻が群生する地蔵川は、居醒の清水から湧き出ています。水温は年間を通じて14度ほどで安定しており、この清冷な水が水中花の生育を支えています。居醒の清水は平成の名水百選にも選ばれています。',
          source: 'https://www.city.maibara.lg.jp/kanko/guide/spot/sizenyama/14399.html',
          mapQuery: '地蔵川 醒井 米原市',
        },
        {
          id: 'samegai-baikamo',
          x: 0.74,
          y: 0.36,
          title: '梅に似た水中の白い花',
          body: '梅花藻は初夏から晩夏にかけて、梅の花のような白い小さな花を咲かせます。開花は五月ごろから始まり、見ごろは七月初旬から九月中旬ごろ。水面すれすれに白い点が連なる、夏のあいだだけの風景です。',
          source: 'https://www.city.maibara.lg.jp/kanko/guide/spot/sizenyama/14399.html',
          mapQuery: '梅花藻 醒井',
        },
        {
          id: 'samegai-hariyo',
          x: 0.22,
          y: 0.5,
          title: '貴重な淡水魚ハリヨ',
          body: 'この川には梅花藻だけでなく、貴重な淡水魚である「ハリヨ」も生息しています。梅花藻はキンポウゲ科の多年生の水草で、流れに沿って水底に群生します。',
          source: 'https://www.city.maibara.lg.jp/kanko/guide/spot/sizenyama/14399.html',
          mapQuery: '中山道 醒井宿',
        },
      ],
    },
    {
      id: 'ibukiyama',
      name: '伊吹山',
      area: '米原市・盛夏の昼',
      image: '/assets/scenery/shiga/scenes/06_ibukiyama.webp',
      hotspots: [
        {
          id: 'ibuki-saikoho',
          x: 0.46,
          y: 0.4,
          title: '滋賀県でいちばん高い山',
          body: '滋賀県最高峰である伊吹山は、岐阜県との県境にそびえ、伊吹山地の南端に属しながら、あたかも独立峰のような山容を見せます。山頂からは360度見渡せるパノラマが楽しめます。',
          source: 'https://www.city.maibara.lg.jp/mtibuki/index.html',
          mapQuery: '伊吹山 米原市',
        },
        {
          id: 'ibuki-1200m',
          x: 0.74,
          y: 0.5,
          title: '標高1200メートルから上は天然記念物',
          body: '標高1,200メートル以上の「伊吹山頂草原植物群落」は、平成15年7月に国の天然記念物に指定されました。伊吹山にのみ自生する特産種、北方系の植物、多雪型の日本海側の植物、石灰岩を好む植物がみられます。',
          source: 'https://www.city.maibara.lg.jp/mtibuki/mtibuki_enkaku/20353.html',
          mapQuery: '伊吹山頂草原植物群落',
        },
        {
          id: 'ibuki-shokubutsu',
          x: 0.22,
          y: 0.48,
          title: '山の名を持つ植物たち',
          body: '伊吹山には、山の名前がついた固有種が多いのも特色の一つです。こうした植物は、地理的、地質的、気候的な立地条件が重なって伊吹山にみられます。',
          source: 'https://www.city.maibara.lg.jp/mtibuki/mtibuki_enkaku/20353.html',
          mapQuery: '伊吹山 お花畑 山頂',
        },
      ],
    },
    {
      id: 'biwako-yugure',
      name: '琵琶湖の夕暮れ',
      area: '滋賀県全域・晩夏の日没前',
      image: '/assets/scenery/shiga/scenes/07_biwako_yugure.webp',
      hotspots: [
        {
          id: 'biwako-tansuiko',
          x: 0.46,
          y: 0.44,
          title: '日本最大の淡水湖',
          body: '琵琶湖は、滋賀県にある日本最大の淡水湖です。面積は669.26平方キロメートルで、滋賀県の面積のおよそ六分の一を占めます。湖岸線の延長は235.20キロメートル、最大水深は103.58メートルあります。',
          source: 'https://www.pref.shiga.lg.jp/de51/2878.html',
          mapQuery: '琵琶湖 滋賀県',
        },
        {
          id: 'biwako-kodaiko',
          x: 0.74,
          y: 0.34,
          title: '四百万年の古代湖',
          body: '琵琶湖はおよそ四百万年もの長い歴史をもつ日本最古の湖とされ、世界中に二十ほど存在する古代湖のうちの一つです。少なくともおよそ四十万年間、この場所に定まっているとされています。',
          source: 'https://www.pref.shiga.lg.jp/de51/2878.html',
          mapQuery: '琵琶湖 古代湖',
        },
        {
          id: 'biwako-mizugame',
          x: 0.22,
          y: 0.5,
          title: '千四百五十万人分の水がめ',
          body: '貯水量は275億立方メートル。これは琵琶湖の水を利用する淀川流域の1450万人が一日に使う水の量の、およそ十一年分に相当します。平均水深は全体でおよそ41.2メートルです。',
          source: 'https://www.pref.shiga.lg.jp/de51/2878.html',
          mapQuery: '琵琶湖 淀川流域',
        },
      ],
    },
    {
      id: 'ishiyamadera',
      name: '石山寺',
      area: '大津市・秋の月夜',
      image: '/assets/scenery/shiga/scenes/08_ishiyamadera.webp',
      hotspots: [
        {
          id: 'ishiyama-tenpyo',
          x: 0.46,
          y: 0.4,
          title: '天平の勅願で開かれた寺',
          body: '石山寺は天平十九年、聖武天皇の勅願で良弁僧正が創建したとされています。西国三十三所観音霊場の第十三番札所で、安産、福徳、厄除け、縁結びの信仰を集めてきました。',
          source: 'https://www.ishiyamadera.or.jp/',
          mapQuery: '石山寺 大津市',
        },
        {
          id: 'ishiyama-keikaiseki',
          x: 0.74,
          y: 0.52,
          title: '石の山の上に建つ寺',
          body: '本堂の前には、天然記念物の硅灰石の大きな岩がそびえています。石山寺はその名の通り石の山の上に建つ寺で、本堂や多宝塔はこの硅灰石の上に建てられています。',
          source: 'https://www.ishiyamadera.or.jp/about/history',
          mapQuery: '石山寺 硅灰石',
        },
        {
          id: 'ishiyama-hana',
          x: 0.22,
          y: 0.48,
          title: '花の寺とも呼ばれる',
          body: '「花の寺」としても知られ、梅や桜、花菖蒲など四季折々の花や紅葉が境内を彩ります。平安時代には貴族のあいだで石山詣が盛んになり、紫式部は参籠の折に『源氏物語』の着想を得たといわれています。',
          source: 'https://www.ishiyamadera.or.jp/',
          mapQuery: '石山寺 本堂',
        },
      ],
    },
    {
      id: 'metasequoia',
      name: 'メタセコイア並木',
      area: '高島市マキノ町・晩秋',
      image: '/assets/scenery/shiga/scenes/09_metasequoia.webp',
      hotspots: [
        {
          id: 'meta-500bon',
          x: 0.46,
          y: 0.44,
          title: '2.4キロに500本',
          body: '高島市マキノ町の県道沿いに、メタセコイアがおよそ2.4キロメートルにわたっておよそ500本植えられています。一本道の両側からまっすぐ並んだ木が視線の先まで続きます。',
          source: 'https://takashima-kanko.jp/spot/metasequoia.html',
          mapQuery: 'メタセコイア並木 高島市マキノ町',
        },
        {
          id: 'meta-gakudo',
          x: 0.74,
          y: 0.34,
          title: '学童農園から始まった並木',
          body: '昭和56年、学童農園「マキノ土に学ぶ里」の整備事業の一環として、マキノ町果樹生産組合が植えたのが始まりです。その後は組合関係者をはじめとする地域の人々に育まれ、県道でもあわせて植栽が進んで、現在の並木になりました。',
          source: 'https://takashima-kanko.jp/spot/metasequoia.html',
          mapQuery: 'マキノピックランド 高島市',
        },
        {
          id: 'meta-shiki',
          x: 0.22,
          y: 0.5,
          title: '四度姿を変える一本道',
          body: '春の四月下旬から五月は芽吹きから新緑へ、夏はボリュームを増して深い緑に。十一月下旬から十二月上旬にはレンガ色や茶色に紅葉し、冬に枝へ雪が乗ると白銀の風景になります。',
          source: 'https://takashima-kanko.jp/spot/metasequoia.html',
          mapQuery: 'メタセコイア並木 紅葉',
        },
      ],
    },
    {
      id: 'enryakuji',
      name: '比叡山延暦寺 根本中堂',
      area: '大津市・冬の朝',
      image: '/assets/scenery/shiga/scenes/10_enryakuji.webp',
      hotspots: [
        {
          id: 'enryaku-788',
          x: 0.46,
          y: 0.42,
          title: '延暦七年に開かれた総本堂',
          body: '根本中堂は延暦寺の総本堂で、本尊は薬師如来です。延暦寺を開いた伝教大師最澄が延暦七年、788年に創建したとされ、現在の建物は1642年に竣工し、国宝に指定されています。',
          source: 'https://www.hieizan.or.jp/grounds/toudou.html',
          mapQuery: '延暦寺 根本中堂 大津市',
        },
        {
          id: 'enryaku-fumetsu',
          x: 0.74,
          y: 0.34,
          title: '千二百年消えていない灯',
          body: '根本中堂には、千二百年間灯り続けている「不滅の法灯」が安置されています。東塔は延暦寺発祥の地であり、根本中堂を中心とする区域です。',
          source: 'https://www.hieizan.or.jp/grounds/toudou.html',
          mapQuery: '不滅の法灯 延暦寺 東塔',
        },
        {
          id: 'enryaku-sekaiisan',
          x: 0.22,
          y: 0.5,
          title: '世界遺産の構成資産',
          body: '延暦寺は、世界文化遺産「古都京都の文化財（京都市、宇治市、大津市）」を構成する17の資産のひとつです。この遺産は1994年に登録され、資産は京都府の京都市・宇治市と滋賀県大津市にあります。',
          source: 'https://online.bunka.go.jp/special_content/hlink3',
          mapQuery: '古都京都の文化財 延暦寺',
        },
      ],
    },
  ],
};
